/** Know your data + Business profile: the browser side of
 *  backend/chainlit/know_your_data_api.py.
 *
 *  The server writes every word on both pages, in English or Arabic; this only
 *  carries the choice. A user flagged english_only gets `languages: ['en']`
 *  and the pages show no switch.
 */

export type Lang = 'en' | 'ar';

export interface Ask {
  question: string;
}

export interface BarRow {
  label: string;
  value: number;
  display: string;
  share: number;
  meta?: string | null;
}

export interface Note {
  title: string;
  text: string;
  so?: string | null;
  week?: { days: string[]; rows: { label: string; open: boolean[] }[] };
  bars?: BarRow[];
}

export type Block =
  | {
      type: 'table';
      head: string[];
      rows: { label: string; value: string; meta: string }[];
    }
  | { type: 'checks'; items: { state: 'ok' | 'warn'; text: string }[] }
  | { type: 'bars'; title: string; caption?: string; rows: BarRow[] }
  | { type: 'callout'; big: string; text: string }
  | { type: 'notes'; items: Note[] }
  | {
      type: 'finding';
      kpi?: string;
      kpi_label?: string;
      title: string;
      text: string;
      ask?: Ask;
      skill?: string;
      source?: string;
      diverging?: {
        reference: number;
        reference_label: string;
        rows: { label: string; value: number; display: string }[];
      };
    }
  | { type: 'event'; title: string; text: string; ask?: Ask }
  | {
      type: 'signal';
      title: string;
      value: string;
      unit: string;
      move: string;
      spark: number[];
      why?: string | null;
      source: string;
    }
  | { type: 'countdown'; value: string; unit: string; text: string }
  | {
      type: 'progress';
      done: number;
      total: number;
      title: string;
      now: string;
      later: string;
      left: string;
      right: string;
    }
  | { type: 'skills'; items: { id: string; title: string; text: string }[] }
  | {
      type: 'blind';
      items: { title: string; text: string; so?: string | null }[];
    }
  | { type: 'questions'; items: Ask[] };

export interface Section {
  id: string;
  title: string;
  intro?: string | null;
  blocks: Block[];
}

export interface KnowYourDataPage {
  building?: boolean;
  lang: Lang;
  dir: 'ltr' | 'rtl';
  languages: Lang[];
  title?: string;
  subtitle?: string;
  sections?: Section[];
  profile_card?: { keys: string[]; names: string[] } | null;
  profile_pending?: number;
}

export type ImportShare = 'low' | 'medium' | 'high';

export interface ProfileValues {
  industry: string | null;
  input_commodities: string[] | null;
  import_share: ImportShare | null;
  fuel_intensive: boolean | null;
  weather_sensitive: boolean | null;
  hospitality_exposed: boolean | null;
  sells_on_credit: boolean | null;
  has_bank_debt: boolean | null;
  prices_follow_usd: boolean | null;
}

export type ProfileField = keyof ProfileValues;

export interface BusinessProfile {
  lang: Lang;
  languages: Lang[];
  values: ProfileValues;
  sources: Partial<Record<ProfileField, 'team' | 'customer'>>;
  pending: number;
  auto_tested: number;
  max_commodities: number;
  updated_at: string | null;
  updated_by: string | null;
  industries: { value: string; label: string }[];
  industry_label: string | null;
  import_shares: { value: ImportShare; label: string }[];
  commodities: Commodity[];
  suggested: { key: string; name: string; groups: string[] }[];
  /** Per question: the indicators an answer brings to the top of what the
   *  chat tests, as the chat names them for the company's country. */
  watches?: Partial<Record<ProfileField, string[]>>;
  /** Questions the company's country has no data for: not shown. */
  hidden?: ProfileField[];
  /** Questions that count as waiting for an answer (the menu badge). */
  asked?: ProfileField[];
}

/** One world price the company may pick, as the Business profile catalog
 *  shows it. The price facts come only with the GET, not with a save. */
export interface Commodity {
  key: string;
  name: string;
  group?: string;
  about?: string | null;
  unit?: string | null;
  last?: number | null;
  /** YYYY-MM of the last price. */
  month?: string | null;
  change_1y?: number | null;
  spark?: number[];
  stale?: boolean;
}

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    const err = await res
      .json()
      .catch(() => ({ detail: `HTTP ${res.status}` }));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

export const fetchKnowYourData = (lang: Lang) =>
  jsonFetch<KnowYourDataPage>(`/api/know-your-data?lang=${lang}`);

export const fetchBusinessProfile = (lang: Lang) =>
  jsonFetch<BusinessProfile>(`/api/business-profile?lang=${lang}`);

export const fetchProfilePending = () =>
  jsonFetch<{ pending: number }>('/api/business-profile/pending');

/** Saves only the fields given; the others stay as they are. */
export const saveBusinessProfile = (
  changes: Partial<ProfileValues>,
  lang: Lang
) =>
  jsonFetch<BusinessProfile>(`/api/business-profile?lang=${lang}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(changes)
  });

// ── The reader's language, kept per browser ──────────────────────────────────

const LANG_KEY = 'signals.kyd.lang';

export function storedLang(): Lang {
  try {
    return window.localStorage.getItem(LANG_KEY) === 'ar' ? 'ar' : 'en';
  } catch {
    return 'en';
  }
}

export function storeLang(lang: Lang) {
  try {
    window.localStorage.setItem(LANG_KEY, lang);
  } catch {
    /* private window: the choice lasts until the tab closes */
  }
}

/** The language to ask for: the stored one, unless this user may not have it. */
export const pickLang = (wanted: Lang, languages?: Lang[]): Lang =>
  languages && !languages.includes(wanted) ? 'en' : wanted;

/** A chat with this question typed in, ready to send. A new conversation:
 *  the caller clears the current one first (as "Ask Signals why" does). */
export const chatHref = (question: string) =>
  `/?prompt=${encodeURIComponent(question.slice(0, 1000))}`;

export const skillHref = (id: string) => `/?skill=${encodeURIComponent(id)}`;
